import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

// Background that grows with the player: at first a faint editor typing out
// nonsense pseudo-code that scrolls up and away; with progress the typing
// speeds up, glitches, drips, and melts into Matrix-style rain.
//
// Preview any stage with a URL parameter, e.g. http://localhost:3000/?bg=0.7

const GLYPHS = '{}[]()<>;:=+-*/&|!?.,$#@%_~^01ｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉ';
const FONT_SIZE = 14;
const LINE_HEIGHT = 20;
const NAVY = { h: 213, s: 41, l: 15 };   // oxford-blue
const FIRST_LINE = '// first day on the job, no mistakes!';

// Keep the counter, button and title readable: the background is faint in the
// middle of the play area and full strength towards the edges.
const CENTER_MASK = 'radial-gradient(ellipse 50% 45% at 50% 40%, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.45) 50%, black 100%)';
const CENTER_FADE = { maskImage: CENTER_MASK, WebkitMaskImage: CENTER_MASK };

const randomGlyph = () => GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
const pick = (list) => list[Math.floor(Math.random() * list.length)];
const clamp = (v, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, v));
const smoothstep = (lo, hi, v) => { const t = clamp((v - lo) / (hi - lo)); return t * t * (3 - 2 * t); };

// 0..1 overall progression: mostly lines/second (log scale), partly rank.
export function backgroundProgress(cps, rank) {
    const cpsPart = clamp(Math.log10(cps + 1) / 7);
    const rankPart = clamp(rank / 10);
    return clamp(cpsPart * 0.65 + rankPart * 0.35);
}

// Earned lines -> code lines typed: exact up to 10, then compressed
// (1,000 -> ~37) and capped at 40 so big numbers stay readable.
function compressLines(amount) {
    if (amount <= 10) return amount;
    return Math.min(40, 10 + 4 * Math.log2(amount / 10));
}

// Code lines typed for a click worth `amount` lines.
export function linesForBurst(amount) {
    return Math.max(1, Math.round(compressLines(amount)));
}

// Code lines typed per second for what workers write per second, on the same
// scale as clicks: 3 lines/s types 3 code lines a second.
export function workerLinesPerSecond(cps) {
    return cps > 0 ? compressLines(cps) : 0;
}

// --- Nonsense pseudo-code -------------------------------------------------

const VERBS = ['fetch', 'sync', 'reticulate', 'defragment', 'hydrate', 'untangle', 'summon', 'refactor', 'yeet', 'deploy', 'bake', 'whisperTo', 'normalize', 'unblock', 'overthink'];
const NOUNS = ['Splines', 'Pixels', 'Synergy', 'Buffer', 'Cache', 'Goats', 'Widget', 'Promise', 'Stack', 'Feelings', 'Coffee', 'Legacy', 'Spaghetti', 'Monolith', 'Vibes'];
const VARS = ['foo', 'bar', 'baz', 'thing', 'stuff', 'temp', 'dataData', 'x', 'legacy', 'ohNo', 'result2', 'final_FINAL', 'it', 'node', 'cfg'];
const METHODS = ['map', 'push', 'flatten', 'frobnicate', 'sprinkle', 'bounce', 'vibe', 'resolve', 'twiddle', 'reduce', 'apologize'];
const PHRASES = ['fix later', 'why does this work', 'do not touch', 'ask Dave', 'this is fine', 'temporary since 2019', 'here be dragons', 'magic number, sorry', 'it compiles, ship it'];
const OPS = ['===', '!==', '>', '<=', '&&', '??'];
const num = () => pick(['0', '1', '42', '7', '1337', '3.14', '-1', '9000', 'NaN']);

function* pseudoCode() {
    for (;;) {
        const fn = `${pick(VERBS)}${pick(NOUNS)}`;
        yield `function ${fn}(${pick(VARS)}, ${pick(VARS)}) {`;
        const body = 2 + Math.floor(Math.random() * 4);
        for (let i = 0; i < body; i++) {
            yield '  ' + pick([
                () => `let ${pick(VARS)} = ${pick(VARS)}.${pick(METHODS)}(${num()});`,
                () => `if (${pick(VARS)} ${pick(OPS)} ${num()}) return ${pick(VARS)};`,
                () => `for (const ${pick(VARS)} of ${pick(VARS)}s) ${pick(VARS)}.${pick(METHODS)}();`,
                () => `// TODO: ${pick(PHRASES)}`,
                () => `${pick(VARS)} = await ${pick(VERBS)}${pick(NOUNS)}(${pick(VARS)});`,
                () => `${pick(VARS)}.${pick(METHODS)}(${pick(VARS)} => ${pick(VARS)} * ${num()});`,
            ])();
        }
        yield `  return ${pick(VARS)};`;
        yield '}';
        yield '';
    }
}

// --- Scene ---------------------------------------------------------------

class Scene {
    constructor() {
        this.time = 0;
        this.progress = 0;
        this.streams = [];
        this.columns = [];
        this.drips = [];
        this.workerRate = 0;   // code lines per second typed for worker output
        this.workerOwed = 0;
        this.width = 0;
        this.height = 0;
        this.charWidth = FONT_SIZE * 0.6;
    }

    resize(width, height, charWidth) {
        this.width = width;
        this.height = height;
        this.charWidth = charWidth;
        this.columns = Array.from({ length: Math.ceil(width / FONT_SIZE) }, () => this.newColumn(true));
        // Up to three editor streams side by side, fewer on narrow screens.
        if (this.streams.length === 0) this.streams = [0, 1, 2].map(i => this.newStream(i));
    }

    newStream(index) {
        // `pending` is how many more code lines this stream still has to type.
        const stream = { index, source: pseudoCode(), lines: [], typedChars: 0, scroll: 0, pending: 0 };
        // A fresh editor: just a note in the first one, then only what you type.
        if (index === 0) stream.lines.push(FIRST_LINE, '');
        stream.lines.push('');
        return stream;
    }

    newColumn(randomStart) {
        const rows = Math.ceil(this.height / FONT_SIZE);
        return {
            row: randomStart ? Math.random() * rows : -Math.random() * 20,
            speed: 0.6 + Math.random() * 0.8,
            seed: Math.random(),
            salt: Math.floor(Math.random() * 1e6),
        };
    }

    activeStreams() {
        const fit = Math.max(1, Math.floor(this.width / 280));
        return this.streams.slice(0, Math.min(fit, 1 + Math.floor(this.progress * 3.2)));
    }

    // Queue code lines to type, spread across the visible editor streams.
    queue(count) {
        const streams = this.activeStreams();
        for (let i = 0; i < count; i++) {
            const stream = streams.reduce((a, b) => (b.pending < a.pending ? b : a));
            stream.pending += 1;
        }
    }

    trailLength() {
        return 6 + Math.round(this.progress * 16);
    }

    update(dt, progress) {
        this.progress = progress;
        this.time += dt;
        const glitch = smoothstep(0.35, 0.95, progress);

        // Workers type their own output continuously; clicks queue bursts on top.
        this.workerOwed += dt * this.workerRate;
        if (this.workerOwed >= 1) {
            this.queue(Math.floor(this.workerOwed));
            this.workerOwed %= 1;
        }

        // Editor typing: only while lines are queued, faster the bigger the
        // backlog, so rapid clicking reads as frantic typing.
        for (const stream of this.activeStreams()) {
            if (stream.pending > 0) {
                stream.typedChars += dt * Math.min(3000, 90 + stream.pending * 45);
            }
            let current = stream.lines[stream.lines.length - 1];
            while (stream.pending > 0 && stream.typedChars >= current.length + 1) {
                stream.typedChars -= current.length + 1;
                // Blank lines are free; everything else uses up a queued line.
                if (current.trim() !== '') stream.pending -= 1;
                stream.lines.push(stream.source.next().value);
                stream.scroll += LINE_HEIGHT;
                current = stream.lines[stream.lines.length - 1];
            }
            // Out of queued lines: wait at the start of a fresh line.
            if (stream.pending === 0) stream.typedChars = 0;
            stream.scroll = Math.max(0, stream.scroll - dt * Math.max(LINE_HEIGHT * 4, stream.scroll * 10));
            const maxLines = Math.ceil(this.height / LINE_HEIGHT) + 2;
            if (stream.lines.length > maxLines) stream.lines.splice(0, stream.lines.length - maxLines);

            // Fever: letters break off the code and fall.
            if (Math.random() < glitch * dt * 25) this.spawnDrip(stream);
        }

        // Matrix rain columns always move; they only show once rain fades in.
        const rows = Math.ceil(this.height / FONT_SIZE);
        const rowsPerSecond = 4 + progress * 26;
        this.columns.forEach((column, i) => {
            column.row += rowsPerSecond * column.speed * dt;
            if (column.row - this.trailLength() > rows) this.columns[i] = this.newColumn(false);
        });

        for (const drip of this.drips) {
            drip.vy += 400 * dt;
            drip.y += drip.vy * dt;
        }
        this.drips = this.drips.filter(d => d.y < this.height + FONT_SIZE).slice(-300);
    }

    spawnDrip(stream) {
        const lineIndex = stream.lines.length - 1 - Math.floor(Math.random() * 8);
        const text = stream.lines[lineIndex];
        if (!text) return;
        const col = Math.floor(Math.random() * text.length);
        if (text[col] === ' ') return;
        this.drips.push({
            x: this.streamX(stream) + col * this.charWidth,
            y: this.lineY(stream, lineIndex),
            vy: 20 + Math.random() * 60,
            ch: text[col],
        });
    }

    streamX(stream) {
        const count = this.activeStreams().length;
        return 24 + stream.index * (this.width / count);
    }

    lineY(stream, lineIndex) {
        const fromBottom = stream.lines.length - 1 - lineIndex;
        return this.height - 48 - fromBottom * LINE_HEIGHT + stream.scroll;
    }

    color(alpha, glitch, offset = 0) {
        // Navy at first; with the fever the hue starts to wander and brighten.
        const hue = NAVY.h + glitch * glitch * 160 * Math.sin(this.time * 0.7 + offset);
        const sat = NAVY.s + glitch * 40;
        const light = NAVY.l + glitch * 35;
        return `hsla(${hue.toFixed(0)}, ${sat.toFixed(0)}%, ${light.toFixed(0)}%, ${alpha.toFixed(3)})`;
    }

    draw(ctx) {
        const p = this.progress;
        const glitch = smoothstep(0.35, 0.95, p);
        ctx.clearRect(0, 0, this.width, this.height);

        // Editor layer: subtle at first, then fades back as the rain takes over.
        const editorAlpha = (0.18 + p * 0.5) * (1 - smoothstep(0.8, 1, p) * 0.5);
        for (const stream of this.activeStreams()) {
            const x0 = this.streamX(stream);
            stream.lines.forEach((text, lineIndex) => {
                const y = this.lineY(stream, lineIndex);
                if (y < -LINE_HEIGHT || y > this.height) return;
                // Lines fade out as they scroll up and away.
                const fade = Math.pow(clamp(y / this.height), 0.9);
                const isCurrent = lineIndex === stream.lines.length - 1;
                const shown = isCurrent ? text.slice(0, Math.floor(stream.typedChars)) : text;
                const wobble = Math.sin(this.time * 3 + lineIndex * 0.9) * glitch * 18;
                ctx.fillStyle = this.color(editorAlpha * fade, glitch, lineIndex * 0.3);

                if (glitch < 0.02) {
                    ctx.fillText(shown, x0, y);
                } else {
                    for (let c = 0; c < shown.length; c++) {
                        const ch = Math.random() < glitch * 0.3 ? randomGlyph() : shown[c];
                        const wave = Math.sin(this.time * 5 + c * 0.45 + lineIndex) * glitch * 5;
                        ctx.fillText(ch, x0 + wobble + c * this.charWidth, y + wave);
                    }
                }
                if (isCurrent && Math.floor(this.time * 2) % 2 === 0) {
                    ctx.fillText('▌', x0 + wobble + shown.length * this.charWidth, y);
                }
            });
        }

        // Letters dripping off the code.
        for (const drip of this.drips) {
            ctx.fillStyle = this.color(0.5 * clamp(1.3 - drip.y / this.height), glitch);
            ctx.fillText(Math.random() < 0.1 ? randomGlyph() : drip.ch, drip.x, drip.y);
        }

        // Matrix rain.
        const rain = smoothstep(0.45, 1, p);
        if (rain > 0) {
            const trail = this.trailLength();
            const tick = Math.floor(this.time * (2 + p * 6));
            this.columns.forEach((column, i) => {
                if (column.seed > rain) return;
                const head = Math.floor(column.row);
                const x = i * FONT_SIZE;
                for (let k = 0; k < trail; k++) {
                    const row = head - k;
                    if (row < 0) break;
                    const alpha = rain * 0.55 * (1 - k / trail);
                    // Stable glyph per cell, re-rolled now and then; the head flickers.
                    const roll = k === 0 ? tick : Math.floor((tick + row) / 7);
                    const g = GLYPHS[(column.salt + row * 31 + roll) % GLYPHS.length];
                    ctx.fillStyle = k === 0 ? `rgba(255, 255, 255, ${(alpha * 1.2).toFixed(3)})` : this.color(alpha, glitch, i * 0.05);
                    ctx.fillText(g, x, row * FONT_SIZE);
                }
            });
        }
    }
}

// `ref.current.burst(amount)` types out the lines a click just earned.
const CodeRain = forwardRef(function CodeRain({ progress, cps }, ref) {
    const canvasRef = useRef(null);
    const sceneRef = useRef(null);
    const progressRef = useRef(progress);
    progressRef.current = progress;
    const cpsRef = useRef(cps);
    cpsRef.current = cps;

    useImperativeHandle(ref, () => ({
        burst: (amount) => sceneRef.current && sceneRef.current.queue(linesForBurst(amount)),
    }), []);

    useEffect(() => {
        const canvas = canvasRef.current;
        // Purely decorative: skip it where canvas or these APIs aren't available (e.g. tests).
        const ctx = canvas.getContext && canvas.getContext('2d');
        if (!ctx || !window.ResizeObserver) return;
        const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const override = parseFloat(new URLSearchParams(window.location.search).get('bg'));
        const currentProgress = () => Number.isNaN(override) ? progressRef.current : clamp(override);

        const scene = new Scene();
        sceneRef.current = scene;
        let frame;
        let last = performance.now();

        const resize = () => {
            const ratio = window.devicePixelRatio || 1;
            const { width, height } = canvas.getBoundingClientRect();
            canvas.width = width * ratio;
            canvas.height = height * ratio;
            ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
            ctx.font = `${FONT_SIZE}px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace`;
            ctx.textBaseline = 'top';
            scene.resize(width, height, ctx.measureText('M').width);
            scene.update(0, currentProgress());
            scene.draw(ctx);
        };

        const loop = (now) => {
            frame = requestAnimationFrame(loop);
            const dt = Math.min(0.1, (now - last) / 1000);
            last = now;
            scene.workerRate = workerLinesPerSecond(cpsRef.current);
            scene.update(dt, currentProgress());
            scene.draw(ctx);
        };

        resize();
        const observer = new ResizeObserver(resize);
        observer.observe(canvas);
        if (!reducedMotion) frame = requestAnimationFrame(loop);

        // Dev-only handle for stepping the scene by hand.
        if (process.env.NODE_ENV === 'development') {
            window.__codeRain = {
                scene,
                step: (seconds, p) => {
                    for (let t = 0; t < seconds; t += 1 / 30) scene.update(1 / 30, p);
                    scene.draw(ctx);
                },
            };
        }

        return () => {
            cancelAnimationFrame(frame);
            observer.disconnect();
            sceneRef.current = null;
        };
    }, []);

    return <canvas ref={canvasRef} aria-hidden="true" style={CENTER_FADE} className="absolute inset-0 w-full h-full pointer-events-none" />;
});

export default CodeRain;
